import type { RatingSummary } from "@atlas/shared";
import { apiGet } from "../lib/api-client";

// ─── Peak & landmark detail (PLT-2) ──────────────────────────────────────────
// Public read-only endpoints — GET /peaks/:slug and /landmarks/:slug work
// logged-out (feature data is not user data). visits is empty and
// collections' completedCount is 0 when there's no session.

export interface FeatureCollectionMembership {
  slug: string;
  name: string;
  itemCount: number;
  completedCount: number;
}

export interface PeakDetail {
  id: string;
  name: string;
  slug: string;
  elevationM: number;
  prominenceM: number | null;
  latitude: number;
  longitude: number;
  country: string | null;
  region: string | null;
  tags: string[];
  wikiUrl: string | null;
}

export interface PeakVisit {
  id: string;
  summitedAt: string;
  notes: string | null;
  activityId: string | null;
  activityName: string | null;
  /** "checkin" = a photo check-in visit (spec 34). Absent on older API responses. */
  sourceType?: string | null;
}

export interface NearbyPeak {
  id: string;
  name: string;
  slug: string;
  elevationM: number;
  distanceKm: number;
}

export interface PeakTrigPillar {
  id: string;
  name: string;
  slug: string;
  visited: boolean;
}

export interface PeakResponse {
  peak: PeakDetail;
  trigPillar: PeakTrigPillar | null;
  visits: PeakVisit[];
  collections: FeatureCollectionMembership[];
  nearbyUncollected: NearbyPeak[];
  rating: RatingSummary;
}

export function fetchPeak(slug: string): Promise<PeakResponse> {
  return apiGet(`/peaks/${slug}`);
}

export interface LandmarkDetail {
  id: string;
  name: string;
  slug: string;
  category: string;
  latitude: number;
  longitude: number;
  region: string | null;
  country: string | null;
  description: string | null;
}

export interface LandmarkVisit {
  id: string;
  visitedAt: string;
  activityId: string | null;
  activityName: string | null;
}

export interface NearbyLandmark {
  id: string;
  name: string;
  slug: string;
  category: string;
  distanceKm: number;
}

export interface LandmarkPeakLink {
  id: string;
  name: string;
  slug: string;
  elevationM: number;
}

export interface LandmarkResponse {
  landmark: LandmarkDetail;
  /** Trig pillars only: the summit this pillar stands on. */
  peak: LandmarkPeakLink | null;
  visits: LandmarkVisit[];
  collections: FeatureCollectionMembership[];
  nearbyUncollected: NearbyLandmark[];
  rating: RatingSummary;
}

export function fetchLandmark(slug: string): Promise<LandmarkResponse> {
  return apiGet(`/landmarks/${slug}`);
}
